import React, { useEffect, useRef, useState } from 'react';
import { FiCheck, FiPlus, FiX, FiZap } from 'react-icons/fi';
import api from '../../services/api';
import { minimumForwardSchedule, normalizeSchedule } from '../../utils/marketingScheduleForward';
import { planFromTarget, targetFromPlan } from '../../utils/instagramPublishPlan';
import { occupiedInstagramMinutesOnDay, suggestPublishTimeForDay } from '../../utils/instagramScheduleSlots';
import '../../components/Button.css';

export default function InstagramManualPostComposer({ defaultPublishTime, igAutoEnabled, onCreated, onClose }) {
  const defaultTime = defaultPublishTime || '10:00';
  const initialSchedule = minimumForwardSchedule(defaultTime);
  const [productQuery, setProductQuery] = useState('');
  const [productHits, setProductHits] = useState([]);
  const [product, setProduct] = useState(null);
  const [title, setTitle] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [startsOn, setStartsOn] = useState(initialSchedule.startsOn);
  const [publishTime, setPublishTime] = useState(initialSchedule.publishTime);
  const [caption, setCaption] = useState('');
  const [autoPublish, setAutoPublish] = useState(true);
  const [generateAi, setGenerateAi] = useState(true);
  const [publishTarget, setPublishTarget] = useState('feed');
  const [storyLinkUrl, setStoryLinkUrl] = useState('https://4shine.nl');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const searchTimer = useRef(null);
  const scheduleInit = useRef(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (scheduleInit.current) return;
    scheduleInit.current = true;
    const next = minimumForwardSchedule(defaultTime);
    setStartsOn(next.startsOn);
    setPublishTime(next.publishTime);
  }, [defaultTime]);

  const applySchedule = (day, time) => {
    setStartsOn(String(day || '').slice(0, 10));
    setPublishTime(String(time || publishTime).slice(0, 5));
  };

  useEffect(() => {
    const day = String(startsOn || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/marketing/events', { params: { from: day, to: day } });
        const occupied = occupiedInstagramMinutesOnDay(res.data || [], day, defaultTime);
        const suggested = suggestPublishTimeForDay({
          dayIso: day,
          defaultTime,
          occupiedMinutes: occupied,
        });
        if (!cancelled) setPublishTime(suggested);
      } catch {
        /* handmatige tijd blijft staan */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startsOn, defaultTime]);

  useEffect(() => {
    if (!productQuery.trim() || productQuery.length < 2) {
      setProductHits([]);
      return undefined;
    }
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(async () => {
      try {
        const res = await api.get(`/producten/magazijn/zoek?q=${encodeURIComponent(productQuery.trim())}`);
        setProductHits(Array.isArray(res.data) ? res.data.slice(0, 12) : []);
      } catch {
        setProductHits([]);
      }
    }, 280);
    return () => {
      if (searchTimer.current) window.clearTimeout(searchTimer.current);
    };
  }, [productQuery]);

  const pickProduct = async (row) => {
    setProduct(row);
    setProductQuery('');
    setProductHits([]);
    if (!title.trim()) setTitle(`Instagram: ${row.naam}`);
    setBusy(true);
    try {
      const res = await api.get('/marketing/sfeer-image', { params: { product_id: row.id } });
      if (res.data?.image_url) setImageUrl(res.data.image_url);
      if (res.data?.permalink) setStoryLinkUrl(res.data.permalink);
    } catch {
      /* handmatige URL / gallery in feed */
    } finally {
      setBusy(false);
    }
  };

  const clearProduct = () => {
    setProduct(null);
  };

  const onPickImageFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!String(file.type || '').startsWith('image/')) {
      alert('Alleen afbeeldingsbestanden.');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('image', file);
      const res = await api.post('/marketing/instagram/upload-image', form);
      if (res.data?.image_url) setImageUrl(res.data.image_url);
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Upload mislukt');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!product && !imageUrl.trim()) {
      alert('Kies een product, upload een afbeelding, of plak een afbeelding-URL.');
      return;
    }
    const schedule = normalizeSchedule(startsOn, publishTime, defaultTime);

    setBusy(true);
    try {
      const plan = planFromTarget(publishTarget);
      await api.post('/marketing/instagram/create-post', {
        product_id: product?.id || undefined,
        title: title.trim() || undefined,
        instagram_image_url: imageUrl.trim() || undefined,
        starts_on: schedule.startsOn,
        instagram_publish_time: schedule.publishTime,
        caption: caption.trim(),
        instagram_auto_publish: autoPublish,
        approve_instagram: autoPublish && igAutoEnabled,
        generate_ai_caption: plan.feed && generateAi && !caption.trim(),
        instagram_publish_target: targetFromPlan(plan),
        instagram_story_link_url: plan.story ? storyLinkUrl.trim() || undefined : undefined,
      });
      window.dispatchEvent(new Event('marketing-ig-feed-refresh'));
      onCreated?.();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card ig-manual-post-composer" aria-labelledby="ig-manual-post-title">
      <header className="ig-manual-post-header">
        <h2 id="ig-manual-post-title">
          <FiPlus aria-hidden /> Nieuwe Instagram-post
        </h2>
        <button type="button" className="btn-icon" onClick={onClose} aria-label="Sluiten">
          <FiX />
        </button>
      </header>
      <p className="muted ig-manual-post-hint">
        Koppel optioneel een product (sfeerfoto), upload een eigen plaatje, of plak een publieke afbeelding-URL. Standaard
        staat de planning ~15 min vooruit; datum en tijd mag je handmatig ook eerder zetten.
      </p>

      <div className="ig-manual-post-grid">
        <label>
          Product (optioneel)
          {product ? (
            <div className="ig-manual-post-product-picked">
              <span>
                {product.naam}
                {product.sku ? ` · ${product.sku}` : ''}
              </span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={clearProduct}>
                Wissen
              </button>
            </div>
          ) : (
            <>
              <input
                className="form-input"
                placeholder="Zoek op naam, SKU of EAN…"
                value={productQuery}
                onChange={(e) => setProductQuery(e.target.value)}
                autoComplete="off"
              />
              {productHits.length > 0 && (
                <ul className="ig-manual-post-search">
                  {productHits.map((row) => (
                    <li key={row.id}>
                      <button type="button" onClick={() => pickProduct(row)}>
                        {row.naam}
                        {row.sku ? ` (${row.sku})` : ''}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </label>

        <label>
          Titel (intern / feed)
          <input
            className="form-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Bijv. Instagram: rotan stoel"
          />
        </label>

        <div className="ig-manual-post-image-block">
          <span className="ig-manual-post-image-label">Afbeelding</span>
          <div className="ig-manual-post-image-actions">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="ig-manual-post-file-input"
              onChange={onPickImageFile}
            />
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy || uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? 'Uploaden…' : 'Bestand kiezen'}
            </button>
            {imageUrl ? (
              <img src={imageUrl} alt="" className="ig-manual-post-image-preview" />
            ) : null}
          </div>
          <label className="ig-manual-post-image-url">
            Of URL
            <input
              className="form-input"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://… (of auto via product / upload)"
            />
          </label>
        </div>

        <label>
          Datum
          <input
            type="date"
            className="form-input"
            value={startsOn}
            onChange={(e) => applySchedule(e.target.value, publishTime)}
          />
        </label>

        <label>
          Tijd
          <input
            type="time"
            className="form-input"
            value={publishTime}
            onChange={(e) => applySchedule(startsOn, e.target.value)}
          />
        </label>

        <label className="ig-manual-post-caption">
          Caption (optioneel)
          <textarea
            className="form-input ig-preview-caption-input"
            rows={4}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Leeg laten = AI-caption na aanmaken (indien aangevinkt)"
          />
        </label>
      </div>

      {(publishTarget === 'story' || publishTarget === 'both') && (
        <label className="ig-manual-post-story-link">
          Story-link (optioneel)
          <input
            className="form-input"
            type="url"
            value={storyLinkUrl}
            onChange={(e) => setStoryLinkUrl(e.target.value)}
            placeholder="https://www.4shine.nl/…"
          />
          <span className="muted">
            Plak deze URL in Instagram bij “Link toevoegen”. Automatische stories hebben geen klikbare link via Meta’s API.
          </span>
        </label>
      )}

      <fieldset className="ig-manual-post-target">
        <legend>Waar plaatsen?</legend>
        <label className="ig-manual-post-target-option">
          <input
            type="radio"
            name="ig-publish-target"
            value="feed"
            checked={publishTarget === 'feed'}
            onChange={() => setPublishTarget('feed')}
          />
          Feed-post (vierkant / tegel in tijdlijn)
        </label>
        <label className="ig-manual-post-target-option">
          <input
            type="radio"
            name="ig-publish-target"
            value="story"
            checked={publishTarget === 'story'}
            onChange={() => setPublishTarget('story')}
          />
          Story (9:16, 24 uur zichtbaar)
        </label>
        <label className="ig-manual-post-target-option">
          <input
            type="radio"
            name="ig-publish-target"
            value="both"
            checked={publishTarget === 'both'}
            onChange={() => setPublishTarget('both')}
          />
          Feed én Story (zelfde planning)
        </label>
      </fieldset>

      <div className="ig-manual-post-options">
        <label className="marketing-ig-checkbox">
          <input type="checkbox" checked={autoPublish} onChange={(e) => setAutoPublish(e.target.checked)} />
          Automatisch plaatsen op geplande datum/tijd
        </label>
        {!igAutoEnabled && autoPublish && (
          <p className="muted ig-manual-post-warn">
            Instagram-automatisering staat uit — post wordt wel gepland maar niet automatisch verstuurd tot je dat in
            Instellingen aanzet.
          </p>
        )}
        <label className="marketing-ig-checkbox">
          <input
            type="checkbox"
            checked={generateAi}
            onChange={(e) => setGenerateAi(e.target.checked)}
            disabled={caption.trim().length >= 15}
          />
          <FiZap aria-hidden /> AI-caption genereren als caption leeg is
          {publishTarget === 'story' && (
            <span className="muted"> (alleen nodig bij feed — stories hebben geen caption via API)</span>
          )}
        </label>
      </div>

      <div className="ig-manual-post-actions">
        <button type="button" className="btn btn-primary" disabled={busy || uploading} onClick={submit}>
          <FiCheck /> Post aanmaken
        </button>
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>
          Annuleren
        </button>
      </div>
    </section>
  );
}
